/** @format */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SBP from "@/assets/securebypaystack.svg";
import Dollar from "@/assets/icons/payment-icon.svg";
import StripeEmbeddedCheckout from "@/components/StripeEmbeddedCheckout";
import { AUTH_BASE_URL, FRONTEND_URL, PAYSTACK_ID } from "@/config";
import { paymentService } from "@/services/paymentService";
import { paymentStore } from "../store/paymentStore";
import { paymentEvents } from "../utils/paymentEvents";

const INITIAL_POLL_DELAYS = [0, 2000, 4000, 8000];
const FOLLOW_UP_POLL_DELAY = 5000;
const MAX_POLL_ATTEMPTS = 24;
const TERMINAL_STATUSES = new Set(["paid", "failed", "expired", "cancelled"]);

const getNumericValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getQuotedAmount = (paymentData) => {
  const explicitQuotedAmount = getNumericValue(
    paymentData?.quotedAmount ??
      paymentData?.rawPaymentData?.quotedAmount ??
      paymentData?.rawPaymentData?.quoted_amount,
  );

  if (explicitQuotedAmount !== null) {
    return explicitQuotedAmount;
  }

  const legacyMinorAmount = getNumericValue(paymentData?.amount);

  if (legacyMinorAmount !== null) {
    return legacyMinorAmount / 100;
  }

  return null;
};

const formatCurrencyAmount = (amount, currency = "USD") => {
  if (!Number.isFinite(amount)) {
    return "--";
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

/** Stripe return/cancel must match backend FRONTEND_BASE_URL (dev.dockie.ai on local dev). */
const getPaymentCallbackBase = () => {
  if (typeof window === "undefined") {
    return FRONTEND_URL || "";
  }

  const { origin, hostname } = window.location;
  const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";

  if (!isLocalHost) {
    return origin;
  }

  try {
    return new URL(AUTH_BASE_URL).origin;
  } catch {
    return FRONTEND_URL || origin;
  }
};

const buildCallbackUrl = (searchKey) => {
  const baseUrl = getPaymentCallbackBase() || FRONTEND_URL || "";
  const path =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/";
  const url = new URL(path, baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`);
  url.searchParams.set(searchKey, "1");
  return url.toString();
};

const getPaymentErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  if (responseData?.detail) {
    return typeof responseData.detail === "string" ? responseData.detail : JSON.stringify(responseData.detail);
  }

  if (Array.isArray(responseData?.non_field_errors)) {
    return responseData.non_field_errors.join(", ");
  }

  if (Array.isArray(responseData?.errors)) {
    return responseData.errors.join(", ");
  }

  if (error?.message) {
    return error.message;
  }

  return "Unable to initialize payment right now. Please try again.";
};

// Fallback if assets fail to load
const DollarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"
      fill="currentColor"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PaymentModal = () => {
  const [open, setOpen] = useState(paymentStore.getState().isOpen);
  const [paymentData, setPaymentData] = useState(paymentStore.getState().paymentData);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [paymentStatus, setPaymentStatus] = useState("idle");
  const [activeProvider, setActiveProvider] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [stripeCheckout, setStripeCheckout] = useState(null);
  const [stripeCheckoutKey, setStripeCheckoutKey] = useState(0);
  const [isPreparingCheckout, setIsPreparingCheckout] = useState(false);

  const pollTimeoutRef = useRef(null);
  const activeSessionIdRef = useRef(paymentStore.getState().paymentData?.session_id);
  const terminalStatusHandledRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const unsubscribe = paymentStore.subscribe((state) => {
      setOpen(state.isOpen);
      setPaymentData(state.paymentData);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const showToastNotification = useCallback((message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setShowToast(true);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const resetCheckoutState = useCallback(
    (nextPaymentData) => {
      stopPolling();
      terminalStatusHandledRef.current = false;
      activeSessionIdRef.current = nextPaymentData?.session_id || null;
      setPaymentStatus("idle");
      setQuoteData(null);
      setStatusData(null);
      setStripeCheckout(null);
      setStripeCheckoutKey(0);
      setIsPreparingCheckout(false);
      setActiveProvider(null);
    },
    [stopPolling],
  );

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (justOpened) {
      resetCheckoutState(paymentData);
    }

    if (!open) {
      stopPolling();
    }
  }, [open, paymentData, resetCheckoutState, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const closePaymentModal = useCallback(() => {
    stopPolling();
    setStripeCheckout(null);
    paymentEvents.closePaymentModal();
  }, [stopPolling]);

  const refreshPaymentViews = useCallback(() => {
    window.dispatchEvent(new CustomEvent("refreshMessages"));
    window.dispatchEvent(new CustomEvent("refreshChatList"));
  }, []);

  const finalizeSuccessfulPayment = useCallback(
    (message = "Payment successful!") => {
      if (terminalStatusHandledRef.current) {
        return;
      }

      terminalStatusHandledRef.current = true;
      stopPolling();
      setPaymentStatus("paid");
      showToastNotification(message, "success");
      refreshPaymentViews();
      window.setTimeout(() => closePaymentModal(), 1500);
    },
    [closePaymentModal, refreshPaymentViews, showToastNotification, stopPolling],
  );

  const handleTerminalStatus = useCallback(
    (status, latestStatusData) => {
      if (!status) {
        return;
      }

      setPaymentStatus(status);
      setStatusData(latestStatusData || null);

      if (status === "paid") {
        finalizeSuccessfulPayment("Payment confirmed!");
        return;
      }

      stopPolling();

      if (status === "failed") {
        showToastNotification("Payment failed. Please try again.", "error");
        return;
      }

      if (status === "expired") {
        setStripeCheckout(null);
        showToastNotification("This payment session expired. Start a new checkout to continue.", "warning");
        return;
      }

      if (status === "cancelled") {
        setStripeCheckout(null);
        showToastNotification("Payment cancelled.", "warning");
      }
    },
    [finalizeSuccessfulPayment, showToastNotification, stopPolling],
  );

  const pollPaymentStatus = useCallback(
    async (sessionId, attempt = 0) => {
      if (!sessionId) {
        return;
      }

      try {
        const response = await paymentService.getPaymentStatus(sessionId);
        const latestStatusData = response?.data || response;
        const nextStatus = latestStatusData?.status || "pending";

        setStatusData(latestStatusData);
        setPaymentStatus(nextStatus);

        if (TERMINAL_STATUSES.has(nextStatus)) {
          handleTerminalStatus(nextStatus, latestStatusData);
          return;
        }

        if (attempt >= MAX_POLL_ATTEMPTS) {
          stopPolling();
          showToastNotification(
            "We could not confirm this payment yet. Please refresh the status or try again.",
            "warning",
          );
          return;
        }

        const nextDelay = INITIAL_POLL_DELAYS[attempt] ?? FOLLOW_UP_POLL_DELAY;

        pollTimeoutRef.current = window.setTimeout(() => {
          pollPaymentStatus(sessionId, attempt + 1);
        }, nextDelay);
      } catch (error) {
        if (attempt >= MAX_POLL_ATTEMPTS) {
          stopPolling();
          showToastNotification("Unable to check payment status right now. Please try again.", "error");
          return;
        }

        const nextDelay = INITIAL_POLL_DELAYS[attempt] ?? FOLLOW_UP_POLL_DELAY;

        pollTimeoutRef.current = window.setTimeout(() => {
          pollPaymentStatus(sessionId, attempt + 1);
        }, nextDelay);
      }
    },
    [handleTerminalStatus, showToastNotification, stopPolling],
  );

  const startPolling = useCallback(
    (sessionId) => {
      stopPolling();
      pollPaymentStatus(sessionId, 0);
    },
    [pollPaymentStatus, stopPolling],
  );

  const handleLegacyPaystackPayment = useCallback(() => {
    if (!paymentData) {
      showToastNotification("No payment data available.", "error");
      return;
    }

    setActiveProvider("paystack");

    if (!window.PaystackPop) {
      showToastNotification("Payment system unavailable.", "error");
      return;
    }

    if (!PAYSTACK_ID) {
      showToastNotification("Paystack is not configured.", "error");
      return;
    }

    const config = {
      reference: paymentData.reference,
      email: paymentData.email,
      amount: paymentData.amount,
      key: PAYSTACK_ID,
      callback: function (response) {
        if (response.status !== "success") {
          showToastNotification("Payment failed.", "error");
          window.setTimeout(function () {
            closePaymentModal();
          }, 2000);
          return;
        }
        setIsPreparingCheckout(true);
        paymentService
          .verifyPayment(response.reference, paymentData.session_id, paymentData.user)
          .then(function () {
            finalizeSuccessfulPayment();
          })
          .catch(function () {
            showToastNotification("Payment verification failed.", "error");
          })
          .finally(function () {
            setIsPreparingCheckout(false);
          });
      },
      onClose: function () {
        showToastNotification("Payment cancelled.", "warning");
      },
    };

    const handler = window.PaystackPop.setup(config);
    handler.openIframe();
  }, [closePaymentModal, finalizeSuccessfulPayment, paymentData, showToastNotification]);

  const openPaystackCheckout = useCallback(
    async (sessionId, latestQuoteData) => {
      setActiveProvider("paystack");

      if (!window.PaystackPop) {
        throw new Error("Paystack script not loaded");
      }

      if (!PAYSTACK_ID) {
        throw new Error("Paystack is not configured");
      }

      const reference = paymentData?.reference || `dockie_${sessionId}_${Date.now()}`;

      try {
        await paymentService.logPaymentInit({ reference, sessionId });
      } catch (error) {
        console.warn("Unable to log Paystack payment init:", error);
      }

      const handler = window.PaystackPop.setup({
        reference,
        email: paymentData?.email,
        amount: latestQuoteData?.chargedAmountMinor,
        currency: latestQuoteData?.chargedCurrency,
        key: PAYSTACK_ID,
        metadata: {
          ...(paymentData?.metadata || {}),
          session_id: sessionId,
        },
        callback: function (response) {
          const ref = response.reference;
          const sid = sessionId;
          setIsPreparingCheckout(true);
          paymentService
            .verifyTruckingPayment({ reference: ref, sessionId: sid })
            .catch(function (error) {
              if (error?.response?.status !== 409) {
                console.error("Paystack verify failed:", error);
              }
            })
            .finally(function () {
              setIsPreparingCheckout(false);
              startPolling(sid);
            });
        },
        onClose: function () {
          showToastNotification("Payment cancelled.", "warning");
        },
      });

      handler.openIframe();
      startPolling(sessionId);
    },
    [paymentData, showToastNotification, startPolling],
  );

  const openStripeCheckout = useCallback(
    async (sessionId) => {
      if (stripeCheckout?.clientSecret) {
        return;
      }

      setStripeCheckout(null);

      try {
        const response = await paymentService.createStripeCheckoutSession({
          sessionId,
          returnUrl: buildCallbackUrl("payment_return"),
          cancelUrl: buildCallbackUrl("payment_cancel"),
        });

        const checkoutData = response?.data || response;

        if (!checkoutData?.clientSecret) {
          throw new Error("Stripe checkout session did not return a client secret.");
        }

        setStripeCheckoutKey((key) => key + 1);
        setStripeCheckout(checkoutData);
        setPaymentStatus("pending");
        startPolling(sessionId);
      } catch (error) {
        if (error?.response?.status === 409) {
          setPaymentStatus("pending");
          showToastNotification("This payment is already in progress. Checking status now.", "warning");
          startPolling(sessionId);
          return;
        }

        throw error;
      }
    },
    [showToastNotification, startPolling, stripeCheckout?.clientSecret],
  );

  const handlePayment = async () => {
    if (!paymentData) {
      showToastNotification("No payment data available.", "error");
      return;
    }

    if (isPreparingCheckout || stripeCheckout?.clientSecret) {
      return;
    }

    const quotedAmount = getQuotedAmount(paymentData);

    if (quotedAmount === null) {
      handleLegacyPaystackPayment();
      return;
    }

    const sessionId =
      activeSessionIdRef.current ||
      paymentData.session_id ||
      (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `payment_${Date.now()}`);

    activeSessionIdRef.current = sessionId;

    try {
      setIsPreparingCheckout(true);
      setPaymentStatus("initializing");

      const response = await paymentService.createPaymentQuote({
        sessionId,
        quotedAmount,
        quotedCurrency: paymentData.quotedCurrency || "USD",
        paymentPhase: paymentData.paymentPhase || "posting_fee",
      });

      const latestQuoteData = response?.data || response;
      const provider = latestQuoteData?.provider || paymentData.provider;

      setQuoteData(latestQuoteData);
      setStatusData(null);
      setActiveProvider(provider);

      if (provider === "stripe") {
        await openStripeCheckout(sessionId);
        return;
      }

      if (provider === "paystack") {
        await openPaystackCheckout(sessionId, latestQuoteData);
        return;
      }

      throw new Error("Unsupported payment provider returned by backend.");
    } catch (error) {
      console.error("Payment initialization failed:", error);
      setPaymentStatus("failed");
      showToastNotification(getPaymentErrorMessage(error), "error");
    } finally {
      setIsPreparingCheckout(false);
    }
  };

  const handleModalClose = useCallback(async () => {
    const sessionId = activeSessionIdRef.current;
    const shouldCancelStripe =
      activeProvider === "stripe" &&
      sessionId &&
      !TERMINAL_STATUSES.has(paymentStatus) &&
      (stripeCheckout || paymentStatus === "pending" || isPreparingCheckout);

    stopPolling();

    if (shouldCancelStripe) {
      try {
        await paymentService.cancelPaymentStatus(sessionId);
        setPaymentStatus("cancelled");
        showToastNotification("Payment cancelled.", "warning");
      } catch (error) {
        console.error("Failed to cancel Stripe payment:", error);
      }
    }

    closePaymentModal();
  }, [
    activeProvider,
    closePaymentModal,
    isPreparingCheckout,
    paymentStatus,
    showToastNotification,
    stopPolling,
    stripeCheckout,
  ]);

  const displayAmount = useMemo(() => {
    const currentQuotedAmount =
      getNumericValue(statusData?.quotedAmount) ??
      getNumericValue(quoteData?.quotedAmount) ??
      getQuotedAmount(paymentData);

    const currentQuotedCurrency =
      statusData?.quotedCurrency || quoteData?.quotedCurrency || paymentData?.quotedCurrency || "USD";

    return formatCurrencyAmount(currentQuotedAmount || 0, currentQuotedCurrency);
  }, [paymentData, quoteData, statusData]);

  const chargedAmountDetails = useMemo(() => {
    const chargedAmountMinor = getNumericValue(quoteData?.chargedAmountMinor);

    if (chargedAmountMinor === null) {
      return null;
    }

    const chargedCurrency = quoteData?.chargedCurrency || "USD";
    const chargedAmountMajor = chargedAmountMinor / 100;
    const quotedAmount = getNumericValue(quoteData?.quotedAmount);
    const quotedCurrency = quoteData?.quotedCurrency;

    if (
      quotedAmount !== null &&
      chargedCurrency === quotedCurrency &&
      Math.abs(chargedAmountMajor - quotedAmount) < 0.0001
    ) {
      return null;
    }

    return `Gateway charge: ${formatCurrencyAmount(chargedAmountMajor, chargedCurrency)}`;
  }, [quoteData]);

  const statusMessage = useMemo(() => {
    if (paymentStatus === "initializing") {
      return "Preparing secure checkout...";
    }

    if (paymentStatus === "pending") {
      return "Waiting for backend payment confirmation.";
    }

    if (paymentStatus === "paid") {
      return "Payment confirmed. Refreshing your chat.";
    }

    if (paymentStatus === "failed") {
      return "Payment failed. You can retry this payment.";
    }

    if (paymentStatus === "expired") {
      return "Checkout expired. Start again to generate a fresh session.";
    }

    if (paymentStatus === "cancelled") {
      return "Payment was cancelled.";
    }

    return null;
  }, [paymentStatus]);

  const showStripeEmbed =
    activeProvider === "stripe" && Boolean(stripeCheckout?.publishableKey && stripeCheckout?.clientSecret);
  const isProviderResolved = activeProvider === "stripe" || activeProvider === "paystack";

  const payButtonLabel = showStripeEmbed
    ? "Refresh payment status"
    : isPreparingCheckout
      ? "Preparing payment..."
      : paymentStatus === "expired" || paymentStatus === "failed"
        ? "Try again"
        : activeProvider === "stripe"
          ? "Continue with Stripe"
          : isProviderResolved
            ? "Pay now"
            : "Continue to payment";

  return (
    <div>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center p-4 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => void handleModalClose()}
        style={{ zIndex: 99999 }}
      >
        {/* Modal */}
        <div
          className={`bg-white rounded-2xl px-4 sm:px-6 py-3 w-full ${
            showStripeEmbed ? "max-w-4xl" : "max-w-md"
          } mx-auto shadow-xl max-h-[90vh] overflow-y-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between py-3 border-b-[1px] border-[#E4E7EC]">
            <div className="flex items-center gap-2">
              {/* Icon space */}
              <div className="w-6 h-6">{Dollar ? <img alt="dollar" src={Dollar} className="" /> : <DollarIcon />}</div>
              <h2 className="text-[18px] font-semibold text-[#181D27]">Proceed to Payment</h2>
            </div>
            <button
              onClick={() => void handleModalClose()}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Amount */}
          <div className="text-center my-4 sm:my-6">
            <div className="text-3xl sm:text-4xl font-bold text-[#344054] mb-2">{displayAmount}</div>
            <div className="text-[#865503] w-fit px-3 mx-auto text-sm font-medium bg-[#FEF6E7] rounded-[12px]">
              Due Now
            </div>
            {chargedAmountDetails && <p className="text-xs text-[#667085] mt-2">{chargedAmountDetails}</p>}
          </div>

          {/* Description */}
          <p className="text-center text-[#344054] text-sm mb-6 sm:mb-8 px-2">
            This payment confirms your booking and allows us to begin arranging your delivery. The backend will confirm
            the final result before any follow-up action continues.
          </p>

          {/* Payment Options */}
          <div className="space-y-4 mb-4 sm:mb-6 bg-[#F9FAFB] border-[#E4E7EC] border-[1px] rounded-[6px]">
            {/* Posting Fee */}
            <div className="flex items-start gap-3 p-3 sm:p-4">
              {/* Radio button space */}
              <div className="w-5 h-5 mt-0.5 flex-shrink-0">
                <div className="w-5 h-5 rounded-full border-2 border-blue-600 bg-blue-600 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">Posting Fee</h3>
                  <span className="font-semibold text-gray-900 text-sm sm:text-base">{displayAmount}</span>
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  {activeProvider === "stripe"
                    ? "Pay securely with Stripe"
                    : activeProvider === "paystack"
                      ? "Complete payment with Paystack"
                      : "Secure provider selected after quote"}
                </p>
                <p className="text-xs text-gray-500">
                  {activeProvider === "stripe"
                    ? "Embedded checkout opens in this modal"
                    : activeProvider === "paystack"
                      ? "Pay now to secure booking"
                      : "Backend selects the correct payment provider"}
                </p>
              </div>
            </div>
          </div>

          {statusMessage && (
            <div className="mb-4 rounded-lg border border-[#E4E7EC] bg-[#F9FAFB] px-4 py-3">
              <p className="text-sm font-medium text-[#344054] capitalize">Status: {paymentStatus}</p>
              <p className="text-sm text-[#667085] mt-1">{statusMessage}</p>
            </div>
          )}

          {showStripeEmbed && (
            <div className="mb-4 rounded-xl border border-[#E4E7EC] overflow-hidden bg-white">
              <StripeEmbeddedCheckout
                key={`stripe-checkout-${stripeCheckoutKey}`}
                publishableKey={stripeCheckout.publishableKey}
                clientSecret={stripeCheckout.clientSecret}
                onComplete={() => {
                  if (activeSessionIdRef.current) {
                    startPolling(activeSessionIdRef.current);
                  }
                }}
              />
            </div>
          )}

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 mb-4 sm:mb-6">
            {/* Warning icon space */}
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.99967 4.00016C8.36786 4.00016 8.66634 4.29864 8.66634 4.66683V9.3335C8.66634 9.70169 8.36786 10.0002 7.99967 10.0002C7.63148 10.0002 7.33301 9.70169 7.33301 9.3335V4.66683C7.33301 4.29864 7.63148 4.00016 7.99967 4.00016Z"
                fill="#865503"
              />
              <path
                d="M7.16634 11.1668C7.16634 11.6271 7.53944 12.0002 7.99967 12.0002C8.45991 12.0002 8.83301 11.6271 8.83301 11.1668C8.83301 10.7066 8.45991 10.3335 7.99967 10.3335C7.53944 10.3335 7.16634 10.7066 7.16634 11.1668Z"
                fill="#865503"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.33301 8.00016C1.33301 4.31826 4.31778 1.3335 7.99967 1.3335C11.6816 1.3335 14.6663 4.31826 14.6663 8.00016C14.6663 11.6821 11.6816 14.6668 7.99967 14.6668C4.31778 14.6668 1.33301 11.6821 1.33301 8.00016ZM7.99967 2.66683C5.05416 2.66683 2.66634 5.05464 2.66634 8.00016C2.66634 10.9457 5.05416 13.3335 7.99967 13.3335C10.9452 13.3335 13.333 10.9457 13.333 8.00016C13.333 5.05464 10.9452 2.66683 7.99967 2.66683Z"
                fill="#865503"
              />
            </svg>
            <p className="text-sm text-[#344054]">
              If a transporter cannot be secured, your delivery payment will be refunded (minus applicable processing
              costs).
            </p>
          </div>

          {/* Pay Button */}
          <div className="space-y-3">
            <button
              onClick={() => {
                if (showStripeEmbed && activeSessionIdRef.current) {
                  startPolling(activeSessionIdRef.current);
                  return;
                }

                void handlePayment();
              }}
              disabled={isPreparingCheckout}
              className="w-full bg-[#0D5EBA] disabled:bg-[#98A2B3] hover:bg-blue-700 text-white font-medium py-3 sm:py-4 px-4 rounded-lg transition-colors text-sm sm:text-base"
            >
              {payButtonLabel}
            </button>

            {showStripeEmbed && (
              <button
                onClick={() => void handleModalClose()}
                className="w-full border border-[#D0D5DD] text-[#344054] font-medium py-3 px-4 rounded-lg transition-colors text-sm sm:text-base hover:bg-gray-50"
              >
                Cancel payment
              </button>
            )}
          </div>

          {/* Provider Footer */}
          <div className="flex items-center justify-center gap-2 mt-3 sm:mt-4 pb-2">
            {activeProvider === "paystack" && SBP ? (
              <img alt="secure by paystack" src={SBP} className="h-4 sm:h-auto" />
            ) : (
              <span className="text-xs text-gray-500">
                {activeProvider === "stripe"
                  ? "Secured by Stripe"
                  : activeProvider === "paystack"
                    ? "Secured by Paystack"
                    : "Secure payment provider selected after quote"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100000]">
          <div
            className={`px-4 py-3 rounded-lg shadow-lg text-white ${
              toastSeverity === "success"
                ? "bg-green-500"
                : toastSeverity === "error"
                  ? "bg-red-500"
                  : toastSeverity === "warning"
                    ? "bg-yellow-500"
                    : "bg-blue-500"
            }`}
          >
            <div className="flex items-center gap-2">
              <span>{toastMessage}</span>
              <button
                onClick={() => setShowToast(false)}
                className="ml-2 hover:bg-white hover:bg-opacity-20 rounded p-1"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentModal;
